import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDeleteMembershipTypeAllowedTime } from 'src/features/allowedTimes/api/membershipType/useDeleteMembershipTypeAllowedTime';
import { LIST_ALLOWED_TIMES_RESOURCE } from 'src/features/allowedTimes/api/membershipType/useListMembershipTypeAllowedTimes';
import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE } from 'src/features/membershipType/api/useGetMembershipTypeConfiguration';
import { AllowedTimes } from 'src/features/membershipType/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface DeleteAllowedTimeProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowedTime: AllowedTimes;
    children?: React.ReactNode;
}

function DeleteMembershipTypeAllowedTime<TData>({ selectedAllowedTime, children }: DeleteAllowedTimeProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const { day_of_week, id } = selectedAllowedTime;

    const onError = useOnError();

    const { mutate } = useDeleteMembershipTypeAllowedTime({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOWED_TIMES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE] });

            setIsModalOpen(false);
            toast({
                title: formatMessage(allowedTimesMessages.deleteAllowedTimeSuccess),
                description: formatMessage(allowedTimesMessages.deleteAllowedTimeSuccessDescription, {
                    entry: day_of_week && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[day_of_week]),
                }),
            });
        },
        onError: (error) => {
            onError(error, () => null, { inlineErrorStatuses: [] });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...allowedTimesMessages.deleteAllowedTime },
                subTitle: {
                    ...allowedTimesMessages.deleteAllowedTimeSubtitle,
                    values: {
                        entry: (day_of_week && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[day_of_week])) ?? '',
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...allowedTimesMessages.confirmDeleteAllowedTime,
                    values: {
                        entry: (day_of_week && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[day_of_week])) ?? '',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () =>
                    mutate({
                        membershipTypeId,
                        allowedTimeId: id,
                        parkingId,
                    }),
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteMembershipTypeAllowedTime;
